import { createContext, useContext, ReactNode, useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { DateRange } from "react-day-picker";
import { logger } from "@/utils/logger";
import { safeGetItem, safeRemoveItem, safeSetJSON } from "@/utils/safeStorage";

const DATE_RANGE_STORAGE_KEY = "dashboard-date-range";
const QUERY_TIMEOUT_MS = 15000; // 15 second timeout

// Keep flexible types for dashboard data to avoid breaking downstream components
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DashboardDataType = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FeedbackPageType = any;

interface DashboardDataContextType {
  pages: FeedbackPageType[] | undefined;
  pagesLoading: boolean;
  pagesError: Error | null;
  dashboardData: DashboardDataType;
  dashboardLoading: boolean;
  dashboardError: Error | null;
  dateRange: DateRange | undefined;
  setDateRange: (range: DateRange | undefined) => void;
  refetchPages: () => void;
  refetchDashboard: () => void;
  clearDateRange: () => void;
  isReady: boolean;
}

const DashboardDataContext = createContext<DashboardDataContextType | undefined>(undefined);

export const DashboardDataProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const abortControllerRef = useRef<AbortController | null>(null);
  
  // Initialize date range from localStorage with stale check
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    const saved = safeGetItem(DATE_RANGE_STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const from = parsed.from ? new Date(parsed.from) : undefined;
        const to = parsed.to ? new Date(parsed.to) : undefined;
        
        // Clear stale date range if end date is before today
        // This prevents old saved filters from hiding new data
        if (to) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const toDate = new Date(to);
          toDate.setHours(0, 0, 0, 0);
          
          if (toDate < today) {
            logger.info("Clearing stale date range filter", { savedTo: to, today });
            safeRemoveItem(DATE_RANGE_STORAGE_KEY);
            return undefined;
          }
        }
        
        return { from, to };
      } catch (e) {
        logger.warn("Failed to parse saved date range", e);
        return undefined;
      }
    }
    return undefined;
  });

  // Persist date range to localStorage
  useEffect(() => {
    if (dateRange?.from && dateRange?.to) {
      safeSetJSON(DATE_RANGE_STORAGE_KEY, {
        from: dateRange.from.toISOString(),
        to: dateRange.to.toISOString(),
      });
    } else {
      safeRemoveItem(DATE_RANGE_STORAGE_KEY);
    }
  }, [dateRange]);

  // Clear date range method
  const clearDateRange = useCallback(() => {
    setDateRange(undefined);
    safeRemoveItem(DATE_RANGE_STORAGE_KEY);
  }, []);

  // Cancel in-flight queries when user changes or component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [user?.id]);

  // Shared feedback pages query with timeout handling
  const {
    data: pages,
    isLoading: pagesLoading,
    error: pagesError,
    refetch: refetchPages,
  } = useQuery({
    queryKey: ["feedback-pages", user?.id],
    queryFn: async ({ signal }) => {
      if (!user?.id) return [];
      
      const startTime = Date.now();
      try {
        const { data, error } = await supabase
          .from("feedback_pages")
          .select("id, title, slug, description, is_active, passcode, created_at, updated_at")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false })
          .abortSignal(signal);

        const duration = Date.now() - startTime;
        if (duration > 3000) {
          logger.warn("Slow pages query", { duration });
        }

        if (error) {
          // Handle timeout specifically
          if (error.message?.includes('timeout') || error.code === '57014') {
            logger.error("Pages query timed out", { duration });
            throw new Error("Request timed out. Please try again.");
          }
          logger.error("Failed to fetch feedback pages", error);
          throw error;
        }
        return data || [];
      } catch (error) {
        if (signal.aborted) {
          logger.debug("Pages query was cancelled");
          throw new Error("Request cancelled");
        }
        logger.error("Error in feedback pages query", error);
        throw error;
      }
    },
    enabled: !!user,
    staleTime: 60000,
    gcTime: 300000,
    refetchOnMount: false,
    refetchOnReconnect: true,
    retry: (failureCount, error) => {
      // Don't retry on cancellation or auth errors
      if (error?.message?.includes('cancelled') || error?.message?.includes('JWT')) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 8000),
  });

  // Shared dashboard data query with date filtering and timeout handling
  const {
    data: dashboardData,
    isLoading: dashboardLoading,
    error: dashboardError,
    refetch: refetchDashboard,
  } = useQuery({
    queryKey: ["dashboard-data", user?.id, dateRange?.from?.toISOString(), dateRange?.to?.toISOString()],
    queryFn: async ({ signal }) => {
      if (!user) return null;

      const startTime = Date.now();
      
      // Create timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("Dashboard request timed out")), QUERY_TIMEOUT_MS);
      });

      try {
        logger.debug("Fetching dashboard data", {
          userId: user.id,
          dateRange: dateRange ? { from: dateRange.from?.toISOString(), to: dateRange.to?.toISOString() } : 'none'
        });
        
        const fetchPromise = supabase.rpc("get_dashboard_data", {
          user_uuid: user.id,
          start_date: dateRange?.from?.toISOString() || null,
          end_date: dateRange?.to?.toISOString() || null,
        });

        // Race between fetch and timeout
        const { data, error } = await Promise.race([
          fetchPromise,
          timeoutPromise.then(() => { throw new Error("Request timed out"); })
        ]) as Awaited<typeof fetchPromise>;

        const duration = Date.now() - startTime;
        if (duration > 5000) {
          logger.warn("Slow dashboard query", { duration });
        }

        if (error) {
          // Handle timeout specifically
          if (error.message?.includes('timeout') || error.code === '57014') {
            logger.error("Dashboard query timed out", { duration });
            throw new Error("Request timed out. Please try again.");
          }
          logger.error("Failed to fetch dashboard data", error);
          throw error;
        }
        
        const rawData = data as Record<string, unknown>;
        
        // Return empty state if no data
        if (!data) {
          return {
            stats: {
              totalPages: 0,
              activePages: 0,
              inactivePages: 0,
              totalSubmissions: 0,
              submissions24h: 0,
              submissions7d: 0,
              avgSubmissionsPerPage: 0
            },
            sentimentData: null,
            trendingTopics: [],
            recentActivity: []
          };
        }
        
        // Handle both camelCase and snake_case keys from RPC
        return {
          stats: rawData.stats || {
            totalPages: 0,
            activePages: 0,
            inactivePages: 0,
            totalSubmissions: 0,
            submissions24h: 0,
            submissions7d: 0,
            avgSubmissionsPerPage: 0
          },
          sentimentData: rawData.sentimentData || rawData.sentiment_data || null,
          trendingTopics: rawData.trendingTopics || rawData.trending_topics || [],
          recentActivity: rawData.recentActivity || rawData.recent_activity || []
        };
      } catch (error) {
        if (signal.aborted) {
          logger.debug("Dashboard query was cancelled");
          throw new Error("Request cancelled");
        }
        logger.error("Error in dashboard data query", error);
        throw error;
      }
    },
    enabled: !!user,
    staleTime: 60000,
    gcTime: 300000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    refetchInterval: false,
    retry: (failureCount, error) => {
      // Don't retry on cancellation, timeout, or auth errors
      const message = error?.message || '';
      if (message.includes('cancelled') || message.includes('timed out') || message.includes('JWT')) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 8000),
    // Use stale data while revalidating on error
    placeholderData: (previousData) => previousData,
  });

  const contextValue = useMemo(
    () => ({
      pages,
      pagesLoading,
      pagesError: pagesError as Error | null,
      dashboardData,
      dashboardLoading,
      dashboardError: dashboardError as Error | null,
      dateRange,
      setDateRange,
      clearDateRange,
      refetchPages,
      refetchDashboard,
      isReady: !!user && !pagesLoading && !dashboardLoading,
    }),
    [
      pages,
      pagesLoading,
      pagesError,
      dashboardData,
      dashboardLoading,
      dashboardError,
      dateRange,
      setDateRange,
      clearDateRange,
      refetchPages,
      refetchDashboard,
      user,
    ]
  );

  return (
    <DashboardDataContext.Provider value={contextValue}>
      {children}
    </DashboardDataContext.Provider>
  );
};

export const useDashboardData = () => {
  const context = useContext(DashboardDataContext);
  if (context === undefined) {
    // Return safe defaults during initialization instead of throwing
    logger.warn("useDashboardData called outside provider, returning defaults");
    return {
      pages: [],
      pagesLoading: true,
      pagesError: null,
      dashboardData: null,
      dashboardLoading: true,
      dashboardError: null,
      dateRange: undefined,
      setDateRange: () => {},
      clearDateRange: () => {},
      refetchPages: () => {},
      refetchDashboard: () => {},
      isReady: false,
    };
  }
  return context;
};
